import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Snackbar, Alert, AlertTitle, Button, Box } from '@mui/material';
import {
  isTokenExpired,
  isTokenExpiringSoon,
  getTokenRemainingTime,
  formatRemainingTime,
  handleSessionExpiration,
  validateSession
} from '../utils/tokenValidator';

/**
 * SessionMonitor Component
 *
 * Monitors JWT token expiration and shows notifications to user
 * - Only runs when user is logged in (has valid token)
 * - Checks token every minute
 * - Shows warning when token will expire soon (5 minutes)
 * - Shows error and redirects when token expires
 * - Listens to sessionExpired custom events from interceptors
 * - Does NOT run on login/public pages
 */
const SessionMonitor = () => {
  const location = useLocation();
  const [showWarning, setShowWarning] = useState(false);
  const [showExpired, setShowExpired] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [isMonitoring, setIsMonitoring] = useState(false);

  /**
   * Determine if monitoring should be active
   * @returns {boolean} true if should monitor, false otherwise
   */
  const shouldMonitor = useCallback(() => {
    // List of public routes that don't require monitoring
    const publicRoutes = ['/', '/secured/register', '/accounts/verified'];

    // Check if current route is public
    const isPublicRoute = publicRoutes.some(route =>
      location.pathname === route || location.pathname.startsWith(route)
    );

    if (isPublicRoute) {
      return false; // Don't monitor on public pages
    }

    // Check if user has valid session
    const session = validateSession();

    // Only monitor if user has valid token
    return session.isValid || (!session.isValid && session.remainingTime > 0);
  }, [location.pathname]);

  /**
   * Check token status and update UI
   */
  const checkTokenStatus = useCallback(() => {
    // Don't check if monitoring is disabled
    if (!isMonitoring) {
      return;
    }

    try {
      // Check if token is expired (function will get token from storage)
      if (isTokenExpired()) {
        console.warn('SessionMonitor: Token has expired');
        setShowExpired(true);
        setShowWarning(false);

        // Auto-redirect after showing notification
        setTimeout(() => {
          handleSessionExpiration();
        }, 3000);
        return;
      }

      // Check if token will expire soon (within 5 minutes)
      if (isTokenExpiringSoon(undefined, 5)) {
        const remaining = getTokenRemainingTime();
        setRemainingTime(remaining);
        setShowWarning(true);
        console.warn(`SessionMonitor: Token will expire in ${formatRemainingTime(remaining)}`);
      } else {
        setShowWarning(false);
      }
    } catch (error) {
      console.error('SessionMonitor: Error checking token status:', error);
    }
  }, [isMonitoring]);

  /**
   * Handle manual logout when user clicks "Logout Now"
   */
  const handleLogoutNow = () => {
    handleSessionExpiration();
  };

  /**
   * Listen to sessionExpired custom events from interceptors
   */
  useEffect(() => {
    const handleSessionExpiredEvent = (event) => {
      console.log('SessionMonitor: Received sessionExpired event', event.detail);
      setShowExpired(true);
      setShowWarning(false);
    };

    window.addEventListener('sessionExpired', handleSessionExpiredEvent);

    return () => {
      window.removeEventListener('sessionExpired', handleSessionExpiredEvent);
    };
  }, []);

  /**
   * Set up interval to check token every minute (only when monitoring is enabled)
   */
  useEffect(() => {
    if (!isMonitoring) {
      return; // Don't start interval if monitoring is disabled
    }

    // Check immediately when monitoring starts
    checkTokenStatus();

    // Then check every 60 seconds
    const interval = setInterval(() => {
      checkTokenStatus();
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [checkTokenStatus, isMonitoring]);

  /**
   * Update monitoring state based on route and token
   */
  useEffect(() => {
    // Small delay to allow token to be saved after login
    const timer = setTimeout(() => {
      const shouldBeMonitoring = shouldMonitor();
      setIsMonitoring(shouldBeMonitoring);

      if (shouldBeMonitoring) {
        console.log('SessionMonitor: Monitoring enabled for route:', location.pathname);
      } else {
        console.log('SessionMonitor: Monitoring disabled for route:', location.pathname);
        // Clear any existing warnings when monitoring stops
        setShowWarning(false);
        setShowExpired(false);
      }
    }, 100); // 100ms delay to allow localStorage to update

    return () => clearTimeout(timer);
  }, [location.pathname, shouldMonitor]);

  /**
   * Update remaining time display every second when warning is shown
   */
  useEffect(() => {
    if (!showWarning || !isMonitoring) return;

    const interval = setInterval(() => {
      const remaining = getTokenRemainingTime();
      setRemainingTime(remaining);

      // If expired while warning is showing
      if (remaining <= 0) {
        setShowWarning(false);
        setShowExpired(true);
        setTimeout(() => {
          handleSessionExpiration();
        }, 3000);
      }
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, [showWarning, isMonitoring]);

  return (
    <>
      {/* Warning: Token expiring soon */}
      <Snackbar
        open={showWarning}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: 8 }}
      >
        <Alert
          severity="warning"
          variant="filled"
          sx={{ width: '100%', minWidth: '350px' }}
        >
          <AlertTitle>
            <strong>⚠️ Sesi Akan Berakhir</strong>
          </AlertTitle>
          <Box>
            Sesi Anda akan berakhir dalam <strong>{formatRemainingTime(remainingTime)}</strong>
          </Box>
          <Box mt={1}>
            <small>Silakan simpan pekerjaan Anda atau login ulang.</small>
          </Box>
        </Alert>
      </Snackbar>

      {/* Error: Token expired */}
      <Snackbar
        open={showExpired}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 8 }}
      >
        <Alert
          severity="error"
          variant="filled"
          sx={{ width: '100%', minWidth: '400px' }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleLogoutNow}
              sx={{ fontWeight: 'bold' }}
            >
              Login Sekarang
            </Button>
          }
        >
          <AlertTitle>
            <strong>🔒 Sesi Telah Berakhir</strong>
          </AlertTitle>
          <Box>
            Sesi Anda telah berakhir. Silakan login kembali untuk melanjutkan.
          </Box>
          <Box mt={0.5}>
            <small>Anda akan diarahkan ke halaman login dalam 3 detik...</small>
          </Box>
        </Alert>
      </Snackbar>
    </>
  );
};

export default SessionMonitor;
